/**
 * Gift List & Featured Gift Blocks
 *
 * Intercepts all Buy Button form submissions on the page
 * and injects gift products into the cart add request.
 *
 * Supports both Gift List blocks (with child items) and
 * Featured Gift blocks (single gift products).
 *
 * Also monitors cart changes and removes orphaned gifts when
 * the qualifying product is removed from the cart.
 */

(function () {
  'use strict';

  class GiftListManager {
    constructor() {
      this.giftLists = [];
      this.featuredGifts = [];
      this.subscriptionGifts = [];
      this.initialized = false;
      this.originalFetch = null;
      this.lastSubmitContext = null;
      this.submitListenerBound = false;
      this.submitContextTtlMs = 10000;
      this.init();
    }

    init() {
      if (this.initialized) return;

      // Find all gift list blocks on the page
      this.giftLists = document.querySelectorAll('[data-gift-list]');

      // Find all featured gift blocks on the page
      this.featuredGifts = document.querySelectorAll('[data-featured-gift]');

      // Find all subscription perk gift markers on the page
      this.subscriptionGifts = document.querySelectorAll('[data-subscription-gift]');

      if (this.giftLists.length === 0 && this.featuredGifts.length === 0 && this.subscriptionGifts.length === 0) return;

      // Track cart form submissions so we can scope gifts to the correct section/product.
      this.bindSubmitTracking();

      // Override the fetch function to intercept cart requests
      this.overrideFetch();

      this.initialized = true;

      // Check for orphaned gifts on page load (in case user navigates back)
      this.removeOrphanedGifts();
    }

    /**
     * Collects all gift variant IDs from gift-list and featured-gift blocks on the page
     * Includes qualifying product ID for orphan protection
     */
    getGiftItems(context = null) {
      const items = [];

      // Collect from gift list blocks (child items)
      this.giftLists.forEach((giftList) => {
        if (!this.shouldIncludeBlockForContext(giftList, context)) return;

        const qualifyingProductId = giftList.dataset.qualifyingProductId;
        const giftItems = giftList.querySelectorAll('.gift-list__item[data-variant-id]');

        giftItems.forEach((item) => {
          const variantId = parseInt(item.dataset.variantId, 10);
          const giftForProduct = context?.productId || qualifyingProductId || '';
          if (!giftForProduct) return;

          if (variantId && !isNaN(variantId)) {
            items.push({
              id: variantId,
              quantity: 1,
              properties: {
                _gift_list: 'true',
                _gift_for_product: giftForProduct,
              },
            });
          }
        });
      });

      // Collect from featured gift blocks
      this.featuredGifts.forEach((featuredGift) => {
        if (featuredGift.dataset.autoAdd === 'false') return;
        if (!this.shouldIncludeBlockForContext(featuredGift, context)) return;

        const qualifyingProductId = featuredGift.dataset.qualifyingProductId;
        const variantId = parseInt(featuredGift.dataset.variantId, 10);
        const giftForProduct = context?.productId || qualifyingProductId || '';
        if (!giftForProduct) return;

        if (variantId && !isNaN(variantId)) {
          items.push({
            id: variantId,
            quantity: 1,
            properties: {
              _gift_list: 'true',
              _gift_for_product: giftForProduct,
            },
          });
        }
      });

      // Collect from subscription perk gift markers.
      // These only apply when the subscriptions block in their section is
      // currently set to "subscribe" (a one-time selection adds nothing).
      this.subscriptionGifts.forEach((marker) => {
        if (!this.shouldIncludeBlockForContext(marker, context)) return;
        if (!this.isSubscriptionActive(marker.dataset.sectionId)) return;

        const qualifyingProductId = marker.dataset.qualifyingProductId;
        const variantId = parseInt(marker.dataset.variantId, 10);
        const giftForProduct = context?.productId || qualifyingProductId || '';
        if (!giftForProduct) return;

        if (variantId && !isNaN(variantId)) {
          items.push({
            id: variantId,
            quantity: 1,
            properties: {
              _gift_list: 'true',
              _gift_for_product: giftForProduct,
            },
          });
        }
      });

      return items;
    }

    /**
     * Whether the subscriptions block in the given section is currently
     * set to "subscribe". Mirrors how product-buy-button / product-price
     * read the shared subscription state.
     */
    isSubscriptionActive(sectionId) {
      if (!sectionId) return false;

      const state = window.subscriptionStateBySection && window.subscriptionStateBySection[sectionId];
      if (state) return state.type === 'subscribe';

      // Fallback to the global state, then to the DOM selection.
      if (window.subscriptionState) return window.subscriptionState.type === 'subscribe';

      const section =
        document.getElementById('shopify-section-' + sectionId) ||
        document.querySelector(`.subscriptions[data-section-id="${sectionId}"]`)?.closest('.shopify-section');
      if (section) {
        return Boolean(
          section.querySelector('.subscription-option--subscribe.is-selected, .subscription-checkbox-container.is-selected')
        );
      }

      return false;
    }

    bindSubmitTracking() {
      if (this.submitListenerBound) return;
      this.submitListenerBound = true;

      document.addEventListener(
        'submit',
        (event) => {
          const form = event.target;
          if (!this.isCartAddForm(form)) return;

          const context = this.getContextFromForm(form);
          if (context) this.lastSubmitContext = context;
        },
        true
      );
    }

    isCartAddForm(form) {
      if (!(form instanceof HTMLFormElement)) return false;
      if (form.dataset?.type === 'add-to-cart-form') return true;
      if (form.action && form.action.includes('/cart/add')) return true;
      return Boolean(form.closest('product-form'));
    }

    getContextFromForm(form) {
      if (!form) return null;

      const sectionEl = form.closest('.shopify-section');
      const sectionId = sectionEl?.id ? sectionEl.id.replace(/^shopify-section-/, '') : null;
      const productInfo = form.closest('product-info') || sectionEl?.querySelector('product-info');
      let productId = productInfo?.dataset?.productId ? String(productInfo.dataset.productId) : null;

      // Secondary buy buttons (an advertorial CTA, a custom section further down
      // the page) sit outside the product section and carry no product-info of
      // their own. When the page has exactly one product, they can only be for it.
      if (!productId) {
        const productInfos = document.querySelectorAll('product-info[data-product-id]');
        if (productInfos.length === 1) {
          productId = String(productInfos[0].dataset.productId);
        }
      }

      if (!sectionId && !productId) return null;

      return {
        sectionId,
        productId,
        capturedAt: Date.now(),
      };
    }

    getRecentSubmitContext() {
      if (!this.lastSubmitContext) return null;

      if (Date.now() - this.lastSubmitContext.capturedAt > this.submitContextTtlMs) {
        this.lastSubmitContext = null;
        return null;
      }

      return this.lastSubmitContext;
    }

    getPrimaryVariantId(parsedBody) {
      if (!parsedBody) return null;

      if (parsedBody.id) {
        const parsedId = parseInt(parsedBody.id, 10);
        return Number.isNaN(parsedId) ? null : parsedId;
      }

      if (Array.isArray(parsedBody.items)) {
        for (const item of parsedBody.items) {
          if (!item) continue;
          const props = item.properties || {};
          if (props._gift_list === 'true' || props._gift_unlock === 'true') continue;

          const parsedId = parseInt(item.id, 10);
          if (!Number.isNaN(parsedId)) return parsedId;
        }
      }

      return null;
    }

    getContextFromVariantId(variantId) {
      if (!variantId) return null;

      const forms = Array.from(document.querySelectorAll('product-form form, form[action*="/cart/add"]'));
      const matchingForms = forms.filter((form) => {
        const input = form.querySelector('[name="id"]');
        if (!input || !input.value) return false;

        return parseInt(input.value, 10) === variantId;
      });

      if (matchingForms.length !== 1) return null;
      return this.getContextFromForm(matchingForms[0]);
    }

    resolveRequestContext(options, parsedBody) {
      const recentContext = this.getRecentSubmitContext();
      if (recentContext) {
        // Consume submit context once to avoid leaking to unrelated cart/add requests.
        this.lastSubmitContext = null;
        return recentContext;
      }

      const activeForm = document.activeElement?.closest?.('form');
      if (activeForm && this.isCartAddForm(activeForm)) {
        const activeContext = this.getContextFromForm(activeForm);
        if (activeContext) return activeContext;
      }

      const body = options?.body;
      if (body instanceof FormData) {
        const rawId = body.get('id');
        const parsedId = parseInt(rawId, 10);
        if (!Number.isNaN(parsedId)) {
          return this.getContextFromVariantId(parsedId);
        }
        return null;
      }

      const variantId = this.getPrimaryVariantId(parsedBody);
      return this.getContextFromVariantId(variantId);
    }

    shouldIncludeBlockForContext(block, context) {
      if (!context) return false;

      const blockSectionId = block.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') || null;
      const blockProductId = block.dataset?.qualifyingProductId ? String(block.dataset.qualifyingProductId) : null;

      // Product is the real scope boundary: a page can have several buy buttons
      // for the same product spread across sections, and they all earn the gift.
      if (context.productId && blockProductId) {
        return context.productId === blockProductId;
      }

      // Fallback when the product can't be resolved on both sides, e.g. a page
      // with more than one product section.
      if (context.sectionId && blockSectionId) {
        return context.sectionId === blockSectionId;
      }

      if (context.sectionId || context.productId) return false;

      return true;
    }

    /**
     * Override native fetch to intercept cart requests
     */
    overrideFetch() {
      this.originalFetch = window.fetch.bind(window);
      const self = this;

      window.fetch = function (url, options) {
        if (typeof url === 'string') {
          // Intercept cart/add to inject gifts
          if (url.includes('/cart/add')) {
            return self.handleCartAdd(self.originalFetch, url, options);
          }

          // Monitor cart changes to remove orphaned gifts
          if (url.includes('/cart/change') || url.includes('/cart/update')) {
            return self.handleCartChange(self.originalFetch, url, options);
          }
        }

        return self.originalFetch(...arguments);
      };
    }

    /**
     * Handle cart change requests - check for and remove orphaned gifts
     */
    async handleCartChange(originalFetch, url, options) {
      // First, let the original cart change complete
      const response = await originalFetch(url, options);

      // Parse sections from original request (needed for orphan removal response)
      let sections = null;
      let sectionsUrl = null;

      // Check URL query params
      try {
        const urlObj = new URL(url, window.location.origin);
        sections = urlObj.searchParams.get('sections');
        sectionsUrl = urlObj.searchParams.get('sections_url');
      } catch (e) {
        // Ignore URL parsing errors
      }

      // Check body (body takes precedence if present)
      try {
        const body = options?.body;
        if (body && typeof body === 'string') {
          const parsed = JSON.parse(body);
          if (parsed.sections) sections = parsed.sections;
          if (parsed.sections_url) sectionsUrl = parsed.sections_url;
        } else if (body instanceof FormData) {
          const formSections = body.get('sections');
          const formSectionsUrl = body.get('sections_url');
          if (formSections) sections = formSections;
          if (formSectionsUrl) sectionsUrl = formSectionsUrl;
        }
      } catch (e) {
        // Ignore parsing errors
      }

      // Check for and remove orphaned gifts, passing sections for proper response
      const orphanResult = await this.removeOrphanedGifts(sections, sectionsUrl);

      // If no orphans were removed, return original response as-is
      if (!orphanResult.removed) {
        return response;
      }

      // Return the response from orphan removal (includes sections)
      return orphanResult.response;
    }

    /**
     * Handle cart add requests by injecting gift items
     */
    async handleCartAdd(originalFetch, url, options) {
      const requestOptions = options || {};

      // Try to parse the request body
      let body = requestOptions.body;
      let isFormData = body instanceof FormData;
      let parsedBody = null;

      // Handle JSON body
      if (body && typeof body === 'string') {
        try {
          parsedBody = JSON.parse(body);
        } catch (e) {
          // Not JSON, proceed with original request
          return originalFetch(url, requestOptions);
        }
      }

      const requestContext = this.resolveRequestContext(requestOptions, parsedBody);
      const giftItems = this.getGiftItems(requestContext);

      // If no scoped gift items, proceed normally
      if (giftItems.length === 0) {
        return originalFetch(url, requestOptions);
      }

      // Handle JSON body with items array format
      if (parsedBody && parsedBody.items) {
        // Add gifts first, then main items last so main product appears at top of cart
        parsedBody.items = giftItems.concat(parsedBody.items);
        requestOptions.body = JSON.stringify(parsedBody);
        return originalFetch(url, requestOptions);
      }

      // Handle FormData - convert to items format
      if (isFormData) {
        const formDataItems = [];
        const variantId = body.get('id');
        const quantity = body.get('quantity') || 1;

        if (variantId) {
          const mainItem = {
            id: parseInt(variantId, 10),
            quantity: parseInt(quantity, 10),
          };

          // Preserve the selling plan so subscriptions survive the conversion.
          const sellingPlan = body.get('selling_plan');
          if (sellingPlan) {
            const parsedSellingPlan = parseInt(sellingPlan, 10);
            mainItem.selling_plan = Number.isNaN(parsedSellingPlan) ? sellingPlan : parsedSellingPlan;
          }

          // Preserve any line item properties (properties[Key]=Value).
          body.forEach((value, key) => {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match) {
              mainItem.properties = mainItem.properties || {};
              mainItem.properties[match[1]] = value;
            }
          });

          formDataItems.push(mainItem);
        }

        // Add gifts first, then main item last so main product appears at top of cart
        const allItems = giftItems.concat(formDataItems);

        // Get sections parameter if present
        const sections = body.get('sections');
        const sectionsUrl = body.get('sections_url');

        // Convert to JSON format
        const newBody = { items: allItems };
        if (sections) {
          newBody.sections = typeof sections === 'string' ? sections.split(',') : sections;
        }
        if (sectionsUrl) {
          newBody.sections_url = sectionsUrl;
        }

        const newOptions = {
          ...requestOptions,
          headers: {
            ...(requestOptions.headers || {}),
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(newBody),
        };

        return originalFetch(url, newOptions);
      }

      // Handle single item JSON body (id + quantity format)
      if (parsedBody && parsedBody.id) {
        const singleItem = {
          id: parseInt(parsedBody.id, 10),
          quantity: parseInt(parsedBody.quantity, 10) || 1,
        };

        // Handle properties if present
        if (parsedBody.properties) {
          singleItem.properties = parsedBody.properties;
        }

        // Add gifts first, then main item last so main product appears at top of cart
        const allItems = giftItems.concat([singleItem]);

        const newBody = {
          items: allItems,
        };

        if (parsedBody.sections) {
          newBody.sections = parsedBody.sections;
        }
        if (parsedBody.sections_url) {
          newBody.sections_url = parsedBody.sections_url;
        }

        requestOptions.body = JSON.stringify(newBody);
        return originalFetch(url, requestOptions);
      }

      // Fallback: proceed normally
      return originalFetch(url, requestOptions);
    }

    /**
     * Remove orphaned gifts from cart (gifts whose qualifying product was removed)
     */
    async removeOrphanedGifts(sections = null, sectionsUrl = null) {
      try {
        // Fetch current cart
        const response = await this.originalFetch('/cart.js', {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) return { removed: false, response: null };

        const cart = await response.json();

        // Find all product IDs currently in cart (non-gift items)
        const productIdsInCart = new Set();
        cart.items.forEach((item) => {
          // Skip gift items when building the product list
          if (!item.properties || item.properties._gift_list !== 'true') {
            productIdsInCart.add(String(item.product_id));
          }
        });

        // Find orphaned gift items (gifts whose qualifying product is no longer in cart)
        const orphanedGiftKeys = [];
        cart.items.forEach((item) => {
          if (item.properties && item.properties._gift_list === 'true') {
            const qualifyingProductId = item.properties._gift_for_product;
            if (!qualifyingProductId || !productIdsInCart.has(String(qualifyingProductId))) {
              orphanedGiftKeys.push(item.key);
            }
          }
        });

        // Remove orphaned gifts
        if (orphanedGiftKeys.length > 0) {
          const updates = {};
          orphanedGiftKeys.forEach((key) => {
            updates[key] = 0;
          });

          // Build update body with sections for proper cart drawer rendering
          const updateBody = { updates };
          if (sections) {
            updateBody.sections = Array.isArray(sections) ? sections : sections.split(',');
          }
          if (sectionsUrl) {
            updateBody.sections_url = sectionsUrl;
          }

          const updateResponse = await this.originalFetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(updateBody),
          });

          // Dispatch event so theme can update cart UI
          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              detail: { reason: 'orphaned-gifts-removed' },
            })
          );

          // Also trigger Shopify's standard cart events
          document.dispatchEvent(new CustomEvent('cart:change'));

          return { removed: true, response: updateResponse };
        }

        return { removed: false, response: null };
      } catch (error) {
        console.error('Gift List: Error removing orphaned gifts', error);
        return { removed: false, response: null };
      }
    }

    /**
     * Refresh gift lists and featured gifts (for dynamic content updates)
     */
    refresh() {
      this.giftLists = document.querySelectorAll('[data-gift-list]');
      this.featuredGifts = document.querySelectorAll('[data-featured-gift]');
      this.subscriptionGifts = document.querySelectorAll('[data-subscription-gift]');
    }
  }

  // Initialize on DOM ready (only once - prevent duplicate overrides in theme editor)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (!window.giftListManager) {
        window.giftListManager = new GiftListManager();
      } else {
        window.giftListManager.refresh();
      }
    });
  } else {
    if (!window.giftListManager) {
      window.giftListManager = new GiftListManager();
    } else {
      window.giftListManager.refresh();
    }
  }

  // Handle Shopify theme editor events
  document.addEventListener('shopify:section:load', () => {
    if (window.giftListManager) {
      window.giftListManager.refresh();
    } else {
      window.giftListManager = new GiftListManager();
    }
  });

  document.addEventListener('shopify:block:select', () => {
    if (window.giftListManager) {
      window.giftListManager.refresh();
    }
  });
})();
